import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Layout } from './components/layout/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { InvestorsList } from './pages/investors/InvestorsList';
import { InvestorDetail } from './pages/investors/InvestorDetail';
import { InvestorPropertyAccountStatements } from './pages/investors/InvestorPropertyAccountStatements';
import { PropertiesList } from './pages/properties/PropertiesList';
import { PropertyDetail } from './pages/properties/PropertyDetail';
import { Prospects } from './pages/rentals/Prospects';
import { ProspectDetail } from './pages/rentals/ProspectDetail';
import { Tenants } from './pages/rentals/Tenants';
import { TenantDetail } from './pages/rentals/TenantDetail';
import { RentPayments } from './pages/rentals/RentPayments';
import { Applications } from './pages/rentals/Applications';
import { ApplicationDetail } from './pages/rentals/ApplicationDetail';
import { StatsDashboard } from './pages/stats/StatsDashboard';
import { Occupancy } from './pages/stats/Occupancy';
import { Units } from './pages/stats/Units';
import { Revenue } from './pages/stats/Revenue';
import { PricePerM2 } from './pages/stats/PricePerM2';
import { PropertyManagementList } from './pages/property-management/PropertyManagementList';
import { PropertyManagementDetail } from './pages/property-management/PropertyManagementDetail';
import { Users } from './pages/Users';
import { MyAccount } from './pages/MyAccount';
import { InvestorMyPropertyPage } from './pages/InvestorMyPropertyPage';
import { SettingsPage } from './pages/Settings';
import { KYC } from './pages/KYC';
import { KYCDetail } from './pages/KYCDetail';
import { MaintenanceReports } from './pages/maintenance/MaintenanceReports';
import { MaintenanceReportDetail } from './pages/maintenance/MaintenanceReportDetail';
import { MaintenanceAccount } from './pages/maintenance/MaintenanceAccount';
import { MaintenanceSettings } from './pages/maintenance/MaintenanceSettings';
import { MaintenanceReportPushRedirect } from './pages/maintenance/MaintenanceReportPushRedirect';
import { DeliveryInspectionForm } from './pages/DeliveryInspectionForm';
import { DeliveryInspectionList } from './pages/DeliveryInspectionList';
import { isInvestorRoleName, isTenantRoleName, isMaintenanceRoleName } from './utils/investorRole';
import { HomeEntry } from './pages/HomeEntry';
import { TenantPortal } from './pages/tenants/TenantPortal';
import { SystemNotificationSettings } from './pages/SystemNotificationSettings';
import {
  ACCESS_APPLICATIONS,
  ACCESS_INVESTORS,
  ACCESS_MAINTENANCE_REPORTS,
  ACCESS_PROPERTIES,
  ACCESS_PROPERTY_MANAGEMENT,
  ACCESS_RENTALS,
  ACCESS_PROSPECTS,
  ACCESS_TENANTS,
  ACCESS_RENT_PAYMENTS,
  ACCESS_STATS_DASHBOARD,
  ACCESS_STATS_OCCUPANCY,
  ACCESS_STATS_PRICE_M2,
  ACCESS_STATS_REVENUE,
  ACCESS_STATS_UNITS,
  ACCESS_USERS,
} from './hooks/usePermissions';

function AppRoutes() {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const isInversor = isInvestorRoleName(user?.role?.name);
  const isTenant = isTenantRoleName(user?.role?.name);
  const isMaintenanceRole = isMaintenanceRoleName(user?.role?.name);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Inversor: Mi cuenta (incl. detalle de propiedad) y configuración
  const isMiCuentaSection =
    location.pathname === '/mi-cuenta' || location.pathname.startsWith('/mi-cuenta/');
  if (isInversor && !isMiCuentaSection && location.pathname !== '/configuracion') {
    return <Navigate to="/mi-cuenta" replace />;
  }

  // Inquilino: solo portal /inquilino y /configuracion
  if (isTenant && location.pathname !== '/inquilino' && location.pathname !== '/configuracion') {
    return <Navigate to="/inquilino" replace />;
  }

  // Mantenimiento: solo reportes, detalle, perfil de cuenta y contraseña (sin /maintenance/settings ni resto del admin)
  if (isMaintenanceRole) {
    const p = location.pathname;
    const maintenanceAllowed =
      p === '/configuracion' ||
      p === '/maintenance' ||
      p === '/delivery-inspections' ||
      p.startsWith('/maintenance/report/') ||
      p === '/maintenance/account' ||
      p.startsWith('/delivery-inspection/');
    if (!maintenanceAllowed) {
      return <Navigate to="/maintenance" replace />;
    }
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomeEntry />} />
        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        
        {/* Inversionistas */}
        <Route 
          path="/inversionistas" 
          element={<ProtectedRoute permission={[...ACCESS_INVESTORS]}><InvestorsList /></ProtectedRoute>} 
        />
        <Route 
          path="/inversionistas/:id" 
          element={<ProtectedRoute permission={[...ACCESS_INVESTORS]}><InvestorDetail /></ProtectedRoute>} 
        />
        <Route
          path="/inversionistas/:id/estado-de-cuenta/:propertyId"
          element={
            <ProtectedRoute permission={[...ACCESS_INVESTORS]}>
              <InvestorPropertyAccountStatements />
            </ProtectedRoute>
          }
        />
        
        {/* Propiedades */}
        <Route 
          path="/propiedades" 
          element={<ProtectedRoute permission={[...ACCESS_PROPERTIES]}><PropertiesList /></ProtectedRoute>} 
        />
        <Route 
          path="/propiedades/:id" 
          element={<ProtectedRoute permission={[...ACCESS_PROPERTIES]}><PropertyDetail /></ProtectedRoute>} 
        />
        
        {/* Rentas */}
        <Route 
          path="/rentas/prospectos" 
          element={<ProtectedRoute permission={[...ACCESS_PROSPECTS]}><Prospects /></ProtectedRoute>} 
        />
        <Route 
          path="/rentas/prospectos/:id" 
          element={<ProtectedRoute permission={[...ACCESS_PROSPECTS]}><ProspectDetail /></ProtectedRoute>} 
        />
        <Route 
          path="/rentas/inquilinos" 
          element={<ProtectedRoute permission={[...ACCESS_TENANTS]}><Tenants /></ProtectedRoute>} 
        />
        <Route 
          path="/rentas/inquilinos/:id" 
          element={<ProtectedRoute permission={[...ACCESS_TENANTS]}><TenantDetail /></ProtectedRoute>} 
        />
        <Route
          path="/rentas/pagos"
          element={<ProtectedRoute permission={[...ACCESS_RENT_PAYMENTS]}><RentPayments /></ProtectedRoute>}
        />
        <Route 
          path="/rentas/solicitudes" 
          element={<ProtectedRoute permission={[...ACCESS_APPLICATIONS]}><Applications /></ProtectedRoute>} 
        />
        <Route
          path="/rentas/solicitudes/:id"
          element={<ProtectedRoute permission={[...ACCESS_APPLICATIONS]}><ApplicationDetail /></ProtectedRoute>}
        />
        
        {/* Estadísticas */}
        <Route
          path="/estadisticas/dashboard"
          element={
            <ProtectedRoute permission={[...ACCESS_STATS_DASHBOARD]}>
              <StatsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/estadisticas/ocupacion"
          element={
            <ProtectedRoute permission={[...ACCESS_STATS_OCCUPANCY]}>
              <Occupancy />
            </ProtectedRoute>
          }
        />
        <Route
          path="/estadisticas/unidades"
          element={
            <ProtectedRoute permission={[...ACCESS_STATS_UNITS]}>
              <Units />
            </ProtectedRoute>
          }
        />
        <Route
          path="/estadisticas/ingresos-brutos"
          element={
            <ProtectedRoute permission={[...ACCESS_STATS_REVENUE]}>
              <Revenue />
            </ProtectedRoute>
          }
        />
        <Route
          path="/estadisticas/valor-m2"
          element={
            <ProtectedRoute permission={[...ACCESS_STATS_PRICE_M2]}>
              <PricePerM2 />
            </ProtectedRoute>
          }
        />

        {/* Administración de inmuebles */}
        <Route 
          path="/property-management" 
          element={<ProtectedRoute permission={[...ACCESS_PROPERTY_MANAGEMENT]}><PropertyManagementList /></ProtectedRoute>} 
        />
        <Route 
          path="/property-management/:id" 
          element={<ProtectedRoute permission={[...ACCESS_PROPERTY_MANAGEMENT]}><PropertyManagementDetail /></ProtectedRoute>} 
        />
        <Route
          path="/property-management/report/:id"
          element={
            <ProtectedRoute permission={[...ACCESS_PROPERTY_MANAGEMENT]}>
              <MaintenanceReportDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/r/mantenimiento/:id"
          element={
            <ProtectedRoute permission={[...ACCESS_MAINTENANCE_REPORTS, ...ACCESS_PROPERTY_MANAGEMENT]}>
              <MaintenanceReportPushRedirect />
            </ProtectedRoute>
          }
        />

        {/* Usuarios */}
        <Route 
          path="/usuarios" 
          element={<ProtectedRoute permission={[...ACCESS_USERS]}><Users /></ProtectedRoute>} 
        />
        
        {/* Mi Cuenta */}
        <Route 
          path="/mi-cuenta" 
          element={<ProtectedRoute permission="view_investor_account"><MyAccount /></ProtectedRoute>} 
        />
        <Route
          path="/mi-cuenta/propiedad/:propertyId"
          element={
            <ProtectedRoute permission="view_investor_account">
              <InvestorMyPropertyPage />
            </ProtectedRoute>
          }
        />

        {/* Portal Inquilino */}
        <Route
          path="/inquilino"
          element={<ProtectedRoute permission="view_tenant_account"><TenantPortal /></ProtectedRoute>}
        />
        
        {/* Configuración (contraseña y 2FA) - cualquier usuario autenticado */}
        <Route path="/configuracion" element={<SettingsPage />} />

        <Route
          path="/configuracion-sistema"
          element={
            <ProtectedRoute permission="manage_settings">
              <SystemNotificationSettings />
            </ProtectedRoute>
          }
        />
        
        {/* KYC / Fideicomiso (requiere permiso view_fideicomiso) */}
        <Route 
          path="/kyc" 
          element={<ProtectedRoute permission="view_fideicomiso"><KYC /></ProtectedRoute>} 
        />
        <Route 
          path="/kyc/:id" 
          element={<ProtectedRoute permission="view_fideicomiso"><KYCDetail /></ProtectedRoute>} 
        />

        {/* Mantenimiento (requiere permiso view_maintenance_reports) */}
        <Route
          path="/maintenance"
          element={<ProtectedRoute permission={[...ACCESS_MAINTENANCE_REPORTS]}><MaintenanceReports /></ProtectedRoute>}
        />
        <Route
          path="/maintenance/report/:id"
          element={<ProtectedRoute permission={[...ACCESS_MAINTENANCE_REPORTS]}><MaintenanceReportDetail /></ProtectedRoute>}
        />
        <Route
          path="/maintenance/account"
          element={<ProtectedRoute permission={[...ACCESS_MAINTENANCE_REPORTS]}><MaintenanceAccount /></ProtectedRoute>}
        />
        <Route
          path="/maintenance/settings"
          element={<ProtectedRoute permission={[...ACCESS_MAINTENANCE_REPORTS]}><MaintenanceSettings /></ProtectedRoute>}
        />
        <Route
          path="/delivery-inspection/:inspectionId"
          element={
            <ProtectedRoute permission={[...ACCESS_MAINTENANCE_REPORTS]}>
              <DeliveryInspectionForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/delivery-inspections"
          element={
            <ProtectedRoute permission={[...ACCESS_MAINTENANCE_REPORTS]}>
              <DeliveryInspectionList />
            </ProtectedRoute>
          }
        />
        
        {/* 404 / default */}
        <Route
          path="*"
          element={
            <Navigate
              to={isInversor ? '/mi-cuenta' : isTenant ? '/inquilino' : isMaintenanceRole ? '/maintenance' : '/'}
              replace
            />
          }
        />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;

